import type {
  QuestDappStoreApp,
  QuestDappStoreBadge,
  QuestDappStorePreview as QuestDappStorePreviewDto,
  QuestDappStoreQuest,
  QuestDappStoreRound,
} from '@src/dto/QuestDappStorePreview';
import { isRight } from 'fp-ts/Either';
import {
  CheckCircle2,
  ChevronRight,
  Circle,
  CircleHelp,
  HelpCircle,
  Loader2,
  Lock,
  RefreshCw,
  Sparkles,
} from 'lucide-react';
import { useEffect, useMemo, useState, type ReactNode } from 'react';

import { Button } from '../../components/ui/button';
import { useTRPC } from '../../hooks/useTRPC';
import { cn } from '../../lib/utils';
import { safeLocalStorage } from '../../utils/safeLocalStorage';
import {
  EditorSection,
  inputClassName,
  minimalButtonClassName,
} from './QuestWorkspaceEditorLayout';

type PreviewTab = 'explore' | 'page' | 'app';

const tabLabels: Record<PreviewTab, string> = {
  explore: 'Explore Card',
  page: 'Quest Page',
  app: 'App Details',
};
const walletStorageKey = 'quests.preview.walletId';

const MAX_BADGES = 8;
const MAX_DAPPS = 4;
const heroBg = 'bg-black text-white';
const heroSurface = 'bg-[#1d2323]';
const heroMuted = 'text-[#778a87]';

function parseDate(value: string | null | undefined) {
  if (!value) {
    return null;
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatQuestDateRange(start: string | null, end: string | null) {
  const startDate = parseDate(start);
  const endDate = parseDate(end);
  if (!startDate || !endDate) {
    return '';
  }

  const formatter = new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
  if (startDate.getUTCMonth() === endDate.getUTCMonth()) {
    return `${formatter.format(startDate)} - ${endDate.getUTCDate()}`;
  }

  return `${formatter.format(startDate)} - ${formatter.format(endDate)}`;
}

function formatRevealDate(value: string | null) {
  const date = parseDate(value);
  if (!date) {
    return '';
  }

  return `${new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'UTC',
  }).format(date)} UTC`;
}

function formatUpdatedAt(value: string | null | undefined) {
  const date = parseDate(value);
  if (!date) {
    return 'Never';
  }

  return new Intl.DateTimeFormat(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
}

function questsLeftText(count: number) {
  return count === 1 ? '1 left' : `${count} left`;
}

function PhoneShell(props: { children: ReactNode }) {
  return (
    <div className="mx-auto w-full max-w-[390px] rounded-[2rem] border border-neutral-700 bg-neutral-950 p-2 shadow-2xl shadow-black/40">
      <div className="min-h-[660px] overflow-hidden rounded-[1.5rem] bg-[#101313] text-white">
        {props.children}
      </div>
    </div>
  );
}

function SummerLogo(props: { className?: string }) {
  return (
    <div
      className={cn(
        'inline-flex flex-col font-black uppercase leading-none',
        props.className,
      )}
    >
      <span className="text-[0.66em] text-[#778a87]">Seeker</span>
      <span className="text-[1em] text-white">Summer Quests</span>
    </div>
  );
}

function RibbonBackdrop() {
  return (
    <div aria-hidden="true" className="absolute inset-0 overflow-hidden">
      <div className="absolute -left-10 top-8 h-36 w-[34rem] rotate-[-10deg] rounded-full border border-white/10" />
      <div className="absolute -left-24 top-28 h-40 w-[38rem] rotate-[-8deg] rounded-full border border-fuchsia-300/20" />
      <div className="absolute right-[-9rem] top-14 size-56 rounded-full border border-orange-300/15" />
    </div>
  );
}

function IconTile(props: { url?: string | null }) {
  return (
    <div className="aspect-square overflow-hidden rounded-2xl bg-white/10">
      {props.url ? (
        <img src={props.url} alt="" className="size-full object-cover" />
      ) : (
        <div className="flex size-full items-center justify-center text-2xl text-white/40">
          ?
        </div>
      )}
    </div>
  );
}

function BadgeTile(props: {
  badge: QuestDappStoreBadge | null;
  alwaysShowRing?: boolean;
}) {
  const earned = props.badge?.earned === true;
  const locked = props.badge?.locked === true;
  const showRing = earned || props.alwaysShowRing;
  const hard = props.badge?.tier === 'Hard';

  return (
    <div className="flex aspect-square items-center justify-center">
      <div className="relative flex size-full items-center justify-center">
        {(showRing || locked || !props.badge) && (
          <span className="absolute inset-0 rounded-full border border-dotted border-white/15" />
        )}
        {showRing && (
          <>
            <span
              className={cn(
                'absolute inset-[10%] rounded-full border border-dotted',
                hard ? 'border-white/80' : 'border-violet-300/80',
              )}
            />
            <span
              className={cn(
                'absolute inset-[4%] animate-spin rounded-full border border-dotted [animation-duration:8s]',
                hard ? 'border-orange-400/70' : 'border-violet-400/60',
              )}
            />
            <span
              className={cn(
                'absolute -inset-[2%] animate-spin rounded-full border border-dotted [animation-duration:12s]',
                hard ? 'border-fuchsia-400/70' : 'border-violet-500/40',
              )}
            />
          </>
        )}
        <div className="relative flex size-[46%] items-center justify-center overflow-hidden rounded-2xl bg-white/10">
          {props.badge?.imageUrl ? (
            <img
              src={props.badge.imageUrl}
              alt=""
              className={cn('size-full object-cover', locked && 'opacity-45')}
            />
          ) : props.badge ? (
            <Sparkles className="size-5 text-white/40" />
          ) : (
            <CircleHelp className="size-5 text-white/40" />
          )}
          {locked && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/70">
              <Lock className="size-4 text-white/60" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function QuestRow(props: { quest: QuestDappStoreQuest }) {
  return (
    <div className="flex items-center gap-2">
      <div className="size-14 shrink-0">
        <BadgeTile
          alwaysShowRing
          badge={{
            badgeKey: props.quest.badgeKey ?? props.quest.id,
            questId: props.quest.id,
            title: props.quest.badgeTitle,
            imageUrl: props.quest.badgeImageUrl,
            tier: props.quest.tier,
            status: props.quest.badgeStatus,
            earned: props.quest.earned,
            locked: false,
            appIndex: 0,
          }}
        />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-base leading-tight text-white">
          {props.quest.title}
        </div>
        {props.quest.description && (
          <div className="mt-1 line-clamp-2 text-xs leading-snug text-[#778a87]">
            {props.quest.description}
          </div>
        )}
      </div>
      {props.quest.earned ? (
        <CheckCircle2 className="size-7 shrink-0 text-[#15994e]" />
      ) : (
        <Circle className="size-7 shrink-0 text-white" />
      )}
    </div>
  );
}

function ExploreCard(props: {
  round: QuestDappStoreRound;
  onOpenPage: () => void;
}) {
  const iconUrls = props.round.apps.map((app) => app.iconUrl).slice(0, 4);

  return (
    <div className="p-4">
      <div
        className={cn(
          'relative overflow-hidden rounded-3xl p-4 shadow-xl shadow-black/40',
          heroBg,
        )}
      >
        <RibbonBackdrop />
        <div className="relative">
          <SummerLogo />
          {props.round.exploreState === 'RoundStarted' ? (
            <>
              <div className="mt-10 text-xs font-semibold uppercase text-[#778a87]">
                Round
              </div>
              <div className="text-4xl font-semibold tabular-nums">
                {String(props.round.roundNumber).padStart(2, '0')}
              </div>
              <div className="mt-24 grid grid-cols-4 gap-2.5">
                {iconUrls.map((url, index) => (
                  <IconTile key={`${url}-${index}`} url={url} />
                ))}
                {Array.from({ length: Math.max(0, 4 - iconUrls.length) }).map(
                  (_, index) => (
                    <IconTile key={index} />
                  ),
                )}
              </div>
              <button
                type="button"
                onClick={props.onOpenPage}
                className="mt-9 flex h-14 w-full items-center justify-center gap-2 rounded-2xl bg-white text-base font-bold text-black"
              >
                Explore Quests
                <ChevronRight className="size-5" />
              </button>
            </>
          ) : props.round.exploreState === 'Announcement' ? (
            <>
              <div className="mt-12 whitespace-pre-line text-3xl font-semibold leading-tight">
                {props.round.rewardSkrLabel} SKR is{'\n'}Dropping for Round{' '}
                {props.round.roundNumber} Participants
              </div>
              <div className="mt-3 text-base leading-relaxed text-[#778a87]">
                Round {props.round.roundNumber} has ended. Claims open{' '}
                {formatRevealDate(props.round.revealStartsAt)}.
              </div>
              <button
                type="button"
                onClick={props.onOpenPage}
                className={cn(
                  'mt-10 flex h-14 w-full items-center justify-center gap-2 rounded-2xl text-base font-bold text-white',
                  heroSurface,
                )}
              >
                View Quests
                <ChevronRight className="size-5" />
              </button>
            </>
          ) : (
            <>
              <div className="mt-20 whitespace-pre-line text-3xl font-semibold leading-tight">
                {props.round.rewardSkrLabel} SKR has dropped for Round{' '}
                {props.round.roundNumber} players
              </div>
              <div className="mt-3 text-base leading-relaxed text-[#778a87]">
                Check your Wallet to see your result.
              </div>
              <button
                type="button"
                className="mt-16 flex h-14 w-full items-center justify-center gap-2 rounded-2xl bg-white text-base font-bold text-black"
              >
                Check in Wallet
                <ChevronRight className="size-5" />
              </button>
              <button
                type="button"
                onClick={props.onOpenPage}
                className={cn(
                  'mt-4 flex h-14 w-full items-center justify-center gap-2 rounded-2xl text-base font-bold text-white',
                  heroSurface,
                )}
              >
                View Quests
                <ChevronRight className="size-5" />
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function badgeColumns(round: QuestDappStoreRound) {
  const groups = new Map<number, QuestDappStoreBadge[]>();
  for (const badge of round.badges.slice(0, MAX_BADGES)) {
    groups.set(badge.appIndex, [...(groups.get(badge.appIndex) ?? []), badge]);
  }

  const columns: Array<Array<QuestDappStoreBadge | null>> = [
    ...groups.values(),
  ];
  const revealed = columns.reduce((sum, column) => sum + column.length, 0);
  for (let remaining = MAX_BADGES - revealed; remaining > 0; remaining -= 2) {
    columns.push(Array.from({ length: Math.min(2, remaining) }, () => null));
  }

  return columns;
}

function QuestAppRow(props: {
  app: QuestDappStoreApp;
  onOpenApp: (app: QuestDappStoreApp) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => props.onOpenApp(props.app)}
      className="flex w-full items-center gap-3 rounded-2xl bg-[#1d2323] p-4 text-left"
    >
      <div className="size-12 shrink-0 overflow-hidden rounded-2xl bg-white/10">
        {props.app.iconUrl && (
          <img
            src={props.app.iconUrl}
            alt=""
            className="size-full object-cover"
          />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-lg font-semibold text-white">
          {props.app.name}
        </div>
        {props.app.curationNote && (
          <div className="truncate text-xs text-[#778a87]">
            {props.app.curationNote}
          </div>
        )}
      </div>
      {props.app.isComplete ? (
        <div className="flex items-center gap-1 text-xs text-[#22c568]">
          Done
          <CheckCircle2 className="size-5 text-[#15994e]" />
        </div>
      ) : (
        <div className="flex items-center gap-1 text-xs text-white">
          {questsLeftText(props.app.questsLeft)}
          <ChevronRight className="size-5" />
        </div>
      )}
    </button>
  );
}

function UnrevealedAppRow() {
  return (
    <div className="flex items-center gap-3 rounded-2xl bg-[#1d2323] p-4">
      <div className="flex size-12 shrink-0 items-center justify-center rounded-2xl bg-[#101313] text-2xl">
        ?
      </div>
      <div className="text-lg font-semibold text-[#778a87]">
        dApp reveal soon...
      </div>
    </div>
  );
}

function QuestPage(props: {
  round: QuestDappStoreRound;
  onOpenApp: (app: QuestDappStoreApp) => void;
}) {
  const columns = badgeColumns(props.round);
  const visibleApps = props.round.apps.slice(0, MAX_DAPPS);
  const hiddenAppCount = Math.max(
    0,
    Math.min(props.round.appCount, MAX_DAPPS) - visibleApps.length,
  );

  return (
    <div className="min-h-[660px] overflow-y-auto bg-[#101313]">
      <div className="relative overflow-hidden bg-black px-4 pb-6 pt-16">
        <RibbonBackdrop />
        <div className="relative">
          <div className="flex items-start justify-between">
            <SummerLogo className="text-2xl" />
            <HelpCircle className="size-7 text-white" />
          </div>
          {props.round.blurb && (
            <div className="mt-6 text-base leading-relaxed text-white">
              {props.round.blurb}
            </div>
          )}
          <div
            className={cn('mt-6 text-xs font-semibold uppercase', heroMuted)}
          >
            Round {String(props.round.roundNumber).padStart(2, '0')}
          </div>
          <div className="mt-1 text-3xl leading-none text-white">
            {formatQuestDateRange(props.round.startsAt, props.round.endsAt)}
          </div>
          <div className="mt-6 text-sm font-semibold uppercase text-white">
            {Math.min(props.round.earnedBadgeCount, MAX_BADGES)} OF {MAX_BADGES}{' '}
            BADGES
          </div>
          <div className="mt-4 space-y-4">
            {columns
              .reduce<Array<Array<Array<QuestDappStoreBadge | null>>>>(
                (rows, column, index) => {
                  const row = Math.floor(index / 4);
                  rows[row] ??= [];
                  rows[row].push(column);
                  return rows;
                },
                [],
              )
              .map((row, rowIndex) => (
                <div key={rowIndex} className="grid grid-cols-4 gap-3">
                  {row.map((column, columnIndex) => (
                    <div key={columnIndex} className="space-y-3">
                      {column.map((badge, badgeIndex) => (
                        <BadgeTile
                          key={badge?.badgeKey ?? badgeIndex}
                          badge={badge}
                        />
                      ))}
                    </div>
                  ))}
                </div>
              ))}
          </div>
        </div>
      </div>

      <div className="px-4 pb-6 pt-6">
        <h4 className="mb-3 text-2xl font-semibold text-white">
          Round {props.round.roundNumber} dApps
        </h4>
        <div className="space-y-3">
          {visibleApps.map((app) => (
            <QuestAppRow
              key={app.androidPackage}
              app={app}
              onOpenApp={props.onOpenApp}
            />
          ))}
          {Array.from({ length: hiddenAppCount }).map((_, index) => (
            <UnrevealedAppRow key={index} />
          ))}
        </div>
      </div>
    </div>
  );
}

function AppDetailsCard(props: {
  app: QuestDappStoreApp;
  round: QuestDappStoreRound;
}) {
  return (
    <div className="min-h-[660px] overflow-y-auto bg-[#101313] p-4">
      <div className="mb-4 flex items-center gap-3">
        <div className="size-14 overflow-hidden rounded-2xl bg-white/10">
          {props.app.iconUrl && (
            <img
              src={props.app.iconUrl}
              alt=""
              className="size-full object-cover"
            />
          )}
        </div>
        <div className="min-w-0">
          <div className="truncate text-xl font-semibold text-white">
            {props.app.name}
          </div>
          <div className="truncate text-xs text-neutral-500">
            {props.app.androidPackage}
          </div>
        </div>
      </div>

      <div className="relative overflow-hidden rounded-3xl bg-black p-4">
        <RibbonBackdrop />
        <div className="relative">
          <div className="flex items-end justify-between gap-3">
            <div>
              <SummerLogo />
              <div className="mt-2 text-sm text-white/70">
                Round {props.round.roundNumber},{' '}
                {formatQuestDateRange(props.round.startsAt, props.round.endsAt)}
              </div>
            </div>
            <HelpCircle className="size-7 shrink-0 text-white" />
          </div>

          {props.app.curationNote && (
            <div className="mt-5 text-base leading-relaxed text-white">
              {props.app.curationNote}
            </div>
          )}

          {props.app.quests.length > 0 && (
            <div className={cn('mt-4 rounded-2xl p-4', heroSurface)}>
              <div className="text-xs font-semibold uppercase text-white/80">
                {props.app.questsLeft} OF {props.app.totalQuests} QUESTS LEFT
              </div>
              <div className="mt-4 space-y-4">
                {props.app.quests.map((quest) => (
                  <QuestRow key={quest.id} quest={quest} />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function QuestDappStorePreview() {
  const trpc = useTRPC();
  const [walletId, setWalletId] = useState(
    () => safeLocalStorage.getItem(walletStorageKey) ?? '',
  );
  const [languageCode, setLanguageCode] = useState('en');
  const [refreshKey, setRefreshKey] = useState(0);
  const [preview, setPreview] = useState<QuestDappStorePreviewDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [tab, setTab] = useState<PreviewTab>('explore');
  const [selectedPackage, setSelectedPackage] = useState<string | null>(null);

  useEffect(() => {
    const interval = window.setInterval(() => {
      setRefreshKey((value) => value + 1);
    }, 15_000);

    return () => window.clearInterval(interval);
  }, []);

  useEffect(() => {
    const value = walletId.trim();
    if (value) {
      safeLocalStorage.setItem(walletStorageKey, value);
    }
  }, [walletId]);

  useEffect(() => {
    let cancelled = false;
    const timeout = window.setTimeout(async () => {
      setIsLoading(true);
      try {
        const result = await trpc.getQuestDappStorePreview.query({
          walletId: walletId.trim() || undefined,
          languageCode: languageCode.trim() || undefined,
        });

        if (cancelled) {
          return;
        }

        if (isRight(result)) {
          setPreview(result.right);
          setError(null);
          setSelectedPackage((current) => {
            const apps = result.right.round?.apps ?? [];
            return apps.some((app) => app.androidPackage === current)
              ? current
              : (apps[0]?.androidPackage ?? null);
          });
        } else {
          setError(result.left.message || 'Could not load preview.');
        }
      } catch {
        if (!cancelled) {
          setError('Could not load preview.');
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }, 300);

    return () => {
      cancelled = true;
      window.clearTimeout(timeout);
    };
  }, [trpc, walletId, languageCode, refreshKey]);

  const selectedApp = useMemo(() => {
    const apps = preview?.round?.apps ?? [];
    return (
      apps.find((app) => app.androidPackage === selectedPackage) ??
      apps[0] ??
      null
    );
  }, [preview, selectedPackage]);

  const renderPhone = () => {
    if (!preview?.round) {
      return (
        <PhoneShell>
          <div className="flex min-h-[660px] items-center justify-center">
            {isLoading ? (
              <Loader2 className="size-8 animate-spin text-neutral-400" />
            ) : (
              <span className="text-sm text-neutral-500">No quest round.</span>
            )}
          </div>
        </PhoneShell>
      );
    }

    return (
      <PhoneShell>
        {tab === 'explore' && (
          <ExploreCard
            round={preview.round}
            onOpenPage={() => setTab('page')}
          />
        )}
        {tab === 'page' && (
          <QuestPage
            round={preview.round}
            onOpenApp={(app) => {
              setSelectedPackage(app.androidPackage);
              setTab('app');
            }}
          />
        )}
        {tab === 'app' && selectedApp && (
          <AppDetailsCard app={selectedApp} round={preview.round} />
        )}
      </PhoneShell>
    );
  };

  return (
    <EditorSection
      title="dApp Store preview"
      description={
        preview
          ? `${preview.sourceEnvironment} quests API - updated ${formatUpdatedAt(
              preview.generatedAt,
            )}`
          : 'Same manifest + catalog flow as the mobile dApp Store'
      }
      actions={
        <Button
          type="button"
          variant="outline"
          size="sm"
          className={cn(minimalButtonClassName, 'shrink-0')}
          onClick={() => setRefreshKey((value) => value + 1)}
          disabled={isLoading}
        >
          {isLoading ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <RefreshCw className="size-4" />
          )}
          Refresh
        </Button>
      }
    >
      <div className="grid gap-5">
        <div className="h-[18rem] space-y-4 overflow-y-auto pr-1">
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="space-y-1 text-xs font-medium text-neutral-300">
              <span>Wallet</span>
              <input
                aria-label="dApp Store wallet"
                value={walletId}
                onInput={(event) => setWalletId(event.currentTarget.value)}
                placeholder="staging defaults to debug wallet"
                className={inputClassName}
              />
            </label>
            <label className="space-y-1 text-xs font-medium text-neutral-300">
              <span>Locale</span>
              <input
                aria-label="dApp Store locale"
                value={languageCode}
                onChange={(event) => setLanguageCode(event.currentTarget.value)}
                placeholder="en"
                className={inputClassName}
              />
            </label>
          </div>

          {preview && (
            <div className="rounded-lg border border-neutral-800 bg-neutral-950/30 px-3 py-2 text-xs text-neutral-400">
              Wallet used: {preview.walletId || 'none'}
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            {(Object.keys(tabLabels) as PreviewTab[]).map((item) => (
              <button
                key={item}
                type="button"
                onClick={() => setTab(item)}
                className={cn(
                  'rounded-full border px-3 py-1.5 text-xs font-medium transition-colors',
                  tab === item
                    ? 'border-teal-400/50 bg-teal-500/15 text-teal-200'
                    : 'border-neutral-800 text-neutral-400 hover:border-neutral-700 hover:text-neutral-200',
                )}
              >
                {tabLabels[item]}
              </button>
            ))}
          </div>

          {preview?.round && (
            <div className="space-y-2">
              <div className="text-xs font-medium uppercase tracking-wide text-neutral-500">
                Apps
              </div>
              <div className="grid gap-2">
                {preview.round.apps.map((app) => (
                  <button
                    key={app.androidPackage}
                    type="button"
                    onClick={() => {
                      setSelectedPackage(app.androidPackage);
                      setTab('app');
                    }}
                    className={cn(
                      'flex items-center justify-between gap-3 rounded-lg border px-3 py-2 text-left text-sm transition-colors',
                      selectedApp?.androidPackage === app.androidPackage
                        ? 'border-teal-500/40 bg-teal-500/10 text-teal-100'
                        : 'border-neutral-800 bg-neutral-950/30 text-neutral-300 hover:border-neutral-700',
                    )}
                  >
                    <span className="min-w-0 truncate">{app.name}</span>
                    <span className="shrink-0 text-xs text-neutral-500">
                      {app.completedQuests}/{app.totalQuests}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {error && (
            <div className="rounded-lg border border-rose-500/30 bg-rose-950/20 px-3 py-2 text-sm text-rose-200">
              {error}
            </div>
          )}
        </div>

        {renderPhone()}
      </div>
    </EditorSection>
  );
}
